import React, { useState, useEffect, useRef } from 'react';
// import BackToServicesButton from '../components/BackToServicesButton';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildTslDoc } from "../pdf/builders/tsl/buildDoc";
import { buildTslWorkbook } from "../excel/builders/tsl/buildWorkbook";

Chart.register(ChartDataLabels);

export default function TslServicePage() {
  const [period, setPeriod] = useState('day');
  const [date1, setDate1] = useState('');
  const [labels, setLabels] = useState([]);
  const [data, setData] = useState([]);
  const [avg, setAvg] = useState(0);
  const [info, setInfo] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [totalSessions, setTotalSessions] = useState(0);
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const locale = useLocale();

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!date1) {
      const today = new Date();
      setDate1(today.toISOString().slice(0, 10));
    }
  }, [date1]);

  const fetchData = async () => {
    if (!isAuth || !radioId || !date1) return;
    setInfo(t('common.loading'));
    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/tsl?period=${period}&date1=${date1}`;
      const resp = await authFetch(url);
      const json = await resp.json();
      if (json.error) {
        setInfo(json.error);
        setLabels([]); setData([]); setAvg(0); setStart(''); setEnd(''); setTotalSessions(0);
        return;
      }
      setLabels(json.labels || []);
      setData(json.data || []);
      setAvg(json.avg || 0);
      setStart(json.start || '');
      setEnd(json.end || '');
      setTotalSessions(json.total_sessions || 0);
      setInfo('');
      setTimeout(() => drawChart(json), 50);
    } catch {
      setInfo(t('tsl.error_generic'));
      setLabels([]); setData([]); setAvg(0); setStart(''); setEnd(''); setTotalSessions(0);
    }
  };

  function drawChart(resp) {
    if (!Chart || !chartRef.current || !resp.data) return;
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) chartInstance.current.destroy();

    let drawLabels = resp.labels || [];
    let drawData = resp.data || [];
    let type = (period === 'day' || period === 'week') ? 'bar' : 'line';

    if (period === 'day') {
      let newLabels = Array(7).fill('');
      let newData = Array(7).fill(0);
      newLabels[3] = drawLabels[0] || '';
      newData[3] = drawData[0] || 0;
      drawLabels = newLabels;
      drawData = newData;
    }

    chartInstance.current = new Chart(ctx, {
      type: type,
      data: {
        labels: drawLabels,
        datasets: [{
          label: t('tsl.dataset_label'),
          data: drawData,
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: 'end',
            align: 'end',
            formatter: value => value ? value : '',
            font: { weight: 'bold' }
          }
        },
        scales: { y: { beginAtZero: true } }
      },
      plugins: [ChartDataLabels]
    });
  }

  const clean = (value) => String(value ?? '').replace(/[^0-9A-Za-z_-]/g, '-');
  const filenameBase = `tsl_${clean(shortcode || 'station')}_${clean(period || 'period')}_${clean(date1 || '')}`;
  const exportDisabled = !data?.length || !!info;
  const exportButtonStyle = calcExportButtonStyle(exportDisabled);

  // Данные загружаются вручную после нажатия кнопки «Показати»
  if (!radioId) {
    return (
      <div style={widgetStyle}>
        {/* BackToServicesButton removed */}
        <h2 style={{ marginBottom: 8 }}>{t('tsl.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('common.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* BackToServicesButton removed */}

      {/* Описание сервиса */}
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 16.5,
          margin: "8px 0 20px 0",
          boxSizing: "border-box",
          whiteSpace: "pre-line",
          lineHeight: 1.65
        }}
      >
        {t('tsl.lead')}
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('tsl.title')}</h2>
      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: PERIOD_BUTTON_GAP
        }}
      >
        <div style={{ display: 'inline-flex', gap: PERIOD_BUTTON_GAP }}>
          <button onClick={() => setPeriod('day')} style={periodBtn(period === 'day')}>{t('tsl.period.day')}</button>
          <button onClick={() => setPeriod('week')} style={periodBtn(period === 'week')}>{t('tsl.period.week')}</button>
          <button onClick={() => setPeriod('month')} style={periodBtn(period === 'month')}>{t('tsl.period.month')}</button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: DATE_FIELD_GAP,
            marginLeft: BUTTONS_DATE_SPACING,
            flexShrink: 0,
            minWidth: DATE_BLOCK_MIN_WIDTH
          }}
        >
          <span style={{ minWidth: DATE_LABEL_MIN_WIDTH, fontWeight: 500, textAlign: 'right' }}>
            {period === 'day' ? t('tsl.date_label_day') : t('tsl.date_label_last')}
          </span>
          <input type="date" value={date1} onChange={e => setDate1(e.target.value)} style={inputStyle}/>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          marginBottom: 14
        }}
      >
        <button
          type="button"
          onClick={fetchData}
          style={showButtonStyle}
        >
          {t('common.show')}
        </button>
        <ExportButton
          data={data}
          onBeforeBuild={async () => {
            const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
            let logoDataUrl = null;
            try {
              const resp = await fetch(logoUrl, { cache: 'no-store' });
              const blob = await resp.blob();
              const reader = new FileReader();
              logoDataUrl = await new Promise((res, rej) => {
                reader.onloadend = () => res(reader.result);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
              });
            } catch {}
            const chartDataUrl =
              (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null;
            return { logoDataUrl, chartDataUrl };
          }}
          buildDoc={(payload) =>
            buildTslDoc({
              t,
              shortcode,
              stationName,
              serviceName: t('tsl.title'),
              period,
              date1,
              labels,
              data,
              avg,
              start,
              end,
              totalSessions,
              fmtNumber,
              locale,
              logoDataUrl: payload?.logoDataUrl || null,
              chartDataUrl: payload?.chartDataUrl || null
            })
          }
          buildWorkbook={(XLSX) =>
            buildTslWorkbook(XLSX, {
              t,
              shortcode,
              stationName,
              serviceName: t('tsl.title'),
              period,
              date1,
              labels,
              data,
              avg,
              start,
              end,
              totalSessions,
              fmtNumber,
              locale
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>
      {info && <div style={{ fontWeight: 500, margin: "18px 0", color: 'red' }}>{info}</div>}
      {!info && (
        <>
          <canvas ref={chartRef} width={500} height={220}
            style={{
              width: '100%',
              height: 220,
              display: 'block',
              border: '1px solid #ddd',
              background: '#fafafa',
              margin: '12px 0',
              borderRadius: 10
            }}
          />
          <div className="tsl-info" style={{
            fontWeight: 'bold',
            marginTop: 8,
            background: '#f4f4fa',
            padding: '8px 10px',
            borderRadius: 7
          }}>
            {totalSessions > 0
              ? <>
                  {t('tsl.avg_prefix')} <b>{fmtNumber(avg, locale)} {t('tsl.minutes')}</b><br />
                  {t('tsl.formula')}
                  <br />
                  {t('tsl.period', { start, end, count: totalSessions, keySeparator: false })}
                </>
              : t('tsl.no_data')}
          </div>
        </>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 960,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const PERIOD_BUTTON_WIDTH = 110;

const periodBtn = active => ({
  background: active ? '#36a2eb' : '#f4f4fa',
  color: active ? '#fff' : '#222',
  border: 'none',
  padding: '7px 18px',
  borderRadius: 5,
  fontWeight: 600,
  cursor: 'pointer',
  width: PERIOD_BUTTON_WIDTH
});

const ACTION_BUTTON_WIDTH = 194;
const ACTION_BUTTON_GAP = 10;
const PERIOD_BUTTON_GAP = 8;
const DATE_FIELD_GAP = 8;
const BUTTONS_DATE_SPACING = -76;
const DATE_BLOCK_MIN_WIDTH = 260;
const DATE_LABEL_MIN_WIDTH = 160;

const baseActionButtonStyle = {
  ...periodBtn(false),
  width: ACTION_BUTTON_WIDTH
};

const showButtonStyle = {
  ...baseActionButtonStyle,
  background: '#3867fa',
  color: '#fff'
};

const calcExportButtonStyle = (disabled) => ({
  ...baseActionButtonStyle,
  background: disabled ? '#9bbdfd' : '#3867fa',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : baseActionButtonStyle.cursor
});
